import {
  BoxV2 as Box,
  Button,
  Flex,
  Table,
  FilterConfig,
  FilterType,
  TableFilter,
  useFilters,
  useTable,
} from 'portal-commons';
import React, { useEffect, useState } from 'react';
import {
  faFilter,
  faArrowDownToLine,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AcceptedRcsCnpCampaignListingRow, ListingTab } from '../components';
import {
  EmptyFetchedData,
  RcsCnpListingTabConfig as ListingTabConfig,
} from '../constants';
import {
  CnpCampaignListItem,
  RcsCnpListingTabKey as ListingTabKey,
} from '../types';
import { CampaignFilterErrorCode as FilterErrorCode } from '../../index';
import { PageTitle } from '@/components';
import Loader from '@/shared_elements/containers/Loader';
import { fieldValidation } from '@/utils/validator';
import { useHistory } from 'react-router-dom';
import { campaignController, enumController } from '@/apis/core';
import { getCnpListOptions, getBillingCategoryOptions } from '@/apis/services';
import { useDispatch } from 'react-redux';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';
import { generateAndDownloadFile } from '@/utils/download';
import { toastFlashMessage } from '@/utils';
import { UsecaseType } from '@/utils/types';

const FilterConfigs: Record<string, FilterConfig> = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Enter campaign id',
    width: 125,
  },
  // displayName: {
  //   type: FilterType.Text,
  //   label: 'Display Name',
  //   placeholder: 'Enter display name',
  //   suggestions: [],
  //   width: 200,
  // },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter brand id',
    width: 150,
  },
  usecase: {
    type: FilterType.Dropdown,
    label: 'Use Case',
    placeholder: 'Select use case',
    options: [],
    width: 250,
  },
  billingCategory: {
    type: FilterType.Dropdown,
    label: 'Billing Category',
    placeholder: 'Select billing category',
    options: [],
    width: 230,
  },
  downstreamCnpUid: {
    type: FilterType.Dropdown,
    label: 'Downstream CNP',
    placeholder: 'Select downstream CNP',
    options: [],
    width: 220,
  },
  status: {
    type: FilterType.Dropdown,
    label: 'TCR Status',
    placeholder: 'Select TCR status',
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    width: 230,
  },
  outSharingStatus: {
    type: FilterType.Dropdown,
    label: 'Sharing Status',
    placeholder: 'Select sharing status',
    options: [
      { label: 'Pending', value: 'PENDING' },
      // { label: 'Rejected', value: 'DECLINED' },
      { label: 'Accepted', value: 'ACCEPTED' },
    ],
    width: 230,
  },
};

const AcceptedHeadRows = [
  { id: 'uid', label: 'Campaign ID', sortable: true },
  // { id: 'displayName', label: 'Display Name', sortable: true },
  { id: 'brandUid', label: 'Brand ID', sortable: true },
  { id: 'usecase', label: 'Use Case', sortable: true },
  { id: 'billingCategory', label: 'Billing Category', sortable: true },
  { id: 'downstreamCnpUid', label: 'Downstream CNP', sortable: false },
  { id: 'sharedOnDate', label: 'Shared On', sortable: true },
  { id: 'upstreamCnpName', label: 'Upstream CNP', sortable: false },
  { id: 'sharedToDate', label: 'Shared To On', sortable: true },
  { id: 'status', label: 'TCR Status', sortable: false },
  { id: 'outSharingStatus', label: 'Sharing Status', sortable: false },
];

export interface ErrorCodeType {
  [name: string]: {
    [key: number | string]: string | boolean | number;
  };
}

const AcceptedCnpRcsCampaigns: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const setLoadingMessage = (message: string) => {
    dispatch({ type: SET_LOADING_MESSAGE, payload: message });
  };
  const clearLoadingMessage = () => {
    dispatch({ type: CLEAR_LOADING_MESSAGE });
  };
  const [rcsUsecaseTypes, setRcsUsecaseTypes] = useState<UsecaseType[]>([]);

  const handleTabChange = (tab: string) => {
    const tabLocation = ListingTabConfig.routes[tab as ListingTabKey];
    if (tabLocation) {
      const { pathname, search } = tabLocation;
      history.push({
        pathname,
        search: search || '',
      });
    }
  };

  const {
    configs: filterConfigs,
    candidateFilters,
    appliedFilters,
    additionalFilters,
    handleEdit: handleFiltersEdit,
    handleApply: handleFiltersApply,
    setOptions: setFilterOptions,
    updateConfigObject: updateFilterConfigs,
  } = useFilters({
    configs: FilterConfigs,
    // loaders: FilterLoaders,
  });
  const [filtersShown, setFiltersShown] = useState(false);
  const fetchCampaigns = async () => {
    const queries = getQueries();

    const response = await campaignController.getRcsCnpCampaigns(queries);
    if (response) {
      return {
        data: response.records,
        rowsPerPage: response.recordsPerPage,
        total: response.totalRecords,
        page: response.page,
      };
    }
    return EmptyFetchedData;
  };

  const {
    loading: tableLoading,
    pageLoading,
    total: totalCampaigns,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    tableData: campaigns,
    pagination,
    filter: tableFilters,
  } = useTable<CnpCampaignListItem>(fetchCampaigns, {
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'createDate',
      ascendingOrder: false,
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getQueries = (): Record<string, any> => {
    return {
      sharingStatus: 'ACCEPTED',
      outSharingStatus: 'PENDING,ACCEPTED',
      actionableOnly: 'false',
      ...tableFilters,
      ...appliedFilters,
      ...additionalFilters,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as Record<string, any>;
  };

  const fetchFilterOptions = async () => {
    const cnpOptions = await getCnpListOptions();
    setFilterOptions('downstreamCnpUid', cnpOptions);

    const billingCategoryOptions = await getBillingCategoryOptions();
    setFilterOptions('billingCategory', billingCategoryOptions);

    const useCaseTypes = await enumController.getUseCases({
      channelId: 'RCS',
    });
    setRcsUsecaseTypes(useCaseTypes);
    const rcsUsecaseOptions = useCaseTypes.map((useCase) => ({
      label: useCase.displayName,
      value: useCase.id,
    }));
    setFilterOptions('usecase', rcsUsecaseOptions);
  };

  const resetFilterConfigsError = (newValues: Record<string, string>) => {
    const tempFilterConfigs = { ...filterConfigs };
    Object.keys(newValues).forEach((key) => {
      if (
        tempFilterConfigs[key]?.error &&
        newValues[key] !== candidateFilters[key]
      ) {
        tempFilterConfigs[key].error = false;
        tempFilterConfigs[key].helperText = '';
      }
    });
    updateFilterConfigs(tempFilterConfigs);
  };

  const validateFilters = (values: Record<string, string>) => {
    const tempFilterConfigs = { ...filterConfigs };
    Object.keys(values).forEach((key) => {
      if (key in FilterErrorCode) {
        const code = fieldValidation({
          ...(FilterErrorCode as ErrorCodeType)[`${key}Obj`],
          fieldval: values[key],
        });
        if (code !== 0) {
          tempFilterConfigs[key].error = true;
          tempFilterConfigs[key].helperText = String(
            (FilterErrorCode as ErrorCodeType)[key][code]
          );
        }
      }
    });
    updateFilterConfigs(tempFilterConfigs);
    return Object.keys(values).every((key) => !tempFilterConfigs[key].error);
  };

  const handleCandidateFiltersChange = (values: Record<string, string>) => {
    resetFilterConfigsError(values);
    handleFiltersEdit(values);
  };

  const handleAppliedFiltersChange = (values: Record<string, string>) => {
    if (!validateFilters(values)) {
      return;
    }

    if (Object.keys(values).length === 0) {
      resetFilterConfigsError(
        filterConfigs as unknown as Record<string, string>
      );
    }
    handleFiltersApply(values);
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const handleDownload = async () => {
    setLoadingMessage('We are generating the document');
    const queries = getQueries();
    const response = await campaignController.downloadRcsCnpCampaigns(queries);
    if (response) {
      const file = new File([response], 'rcs-cnp-campaigns.csv');
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download event history', 'error');
    }
    clearLoadingMessage();
  };

  return (
    <>
      <Box
        sx={{ mt: -5, mb: 20, width: '100%' }}
        data-testid="rcsCampaignsListingTab"
      >
        <ListingTab
          items={ListingTabConfig.items}
          activeTab="accepted-rcs-cnp-campaigns"
          onTabChange={handleTabChange}
        />
      </Box>
      <Box
        as="section"
        sx={{
          // Fix dropdown width affected by global styling
          '& .MuiAutocomplete-root': {
            width: 'unset',
          },
        }}
        data-testid="rcsCampaignListing"
      >
        {pageLoading ? (
          <Loader />
        ) : (
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              rowGap: '12px',
            }}
          >
            <PageTitle>
              {totalCampaigns > 0
                ? `${totalCampaigns} RCS Accepted Connectivity Partner Campaigns`
                : `RCS Accepted Connectivity Partner Campaigns`}
            </PageTitle>
            <Flex
              sx={{
                alignItems: 'center',
                columnGap: '12px',
                width: '100%',
                marginTop: '-8px',
              }}
            >
              <Button
                color="secondary"
                onClick={() => setFiltersShown(!filtersShown)}
                data-testid="tableAddFilterButton"
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>{filtersShown ? 'Hide' : 'Show'} Filters</span>
              </Button>
              <Button
                variant="outline"
                color="secondary"
                onClick={handleDownload}
                data-testid="tableDownloadButton"
              >
                <FontAwesomeIcon icon={faArrowDownToLine} />
                Download
              </Button>
            </Flex>
            {filtersShown && (
              <TableFilter
                configs={filterConfigs}
                candidateValues={candidateFilters}
                appliedValues={appliedFilters}
                onCandidateValuesChange={(values: Record<string, string>) =>
                  handleCandidateFiltersChange(values)
                }
                onAppliedValuesChange={(values: Record<string, string>) =>
                  handleAppliedFiltersChange(values)
                }
              />
            )}
            <Table
              testId="acceptedCnpRcsCampaignsListingTable"
              loading={tableLoading}
              emptyState="no RCS Campaigns to view"
              headRows={AcceptedHeadRows}
              tableData={campaigns.map((campaign: CnpCampaignListItem) => (
                <AcceptedRcsCnpCampaignListingRow
                  key={campaign.uid}
                  campaign={campaign}
                  rcsUseCases={rcsUsecaseTypes}
                />
              ))}
              handleChangePage={handlePageChange}
              createSortHandler={handleSortingChange}
              filter={tableFilters}
              pagination={pagination}
              headerTextTransform="none"
            />
          </Flex>
        )}
      </Box>
    </>
  );
};

export default AcceptedCnpRcsCampaigns;
